class PredictiveSearch extends HTMLElement {
	/**
	 * Constructor to initialize the PredictiveSearch component.
	 */
	constructor() {
		super();

		this.input = this.querySelector('input[type="search"]');
		this.predictiveSearchResults = this.querySelector('#predictive-search');
		this.predictiveSearchDefault = this.querySelector('#predictive-search-default');
		/** @type {AbortController | null} */
		this._suggestAbort = null;

		this.input.addEventListener(
			'input',
			this.debounce((event) => {
				this.onChange(event);
			}, 300).bind(this)
		);
	}

	/**
	 * Event handler for input changes.
	 */
	onChange() {
		const searchTerm = this.input.value.trim();

		if (!searchTerm.length) {
			this.close();
			return;
		}

		this.getSearchResults(searchTerm);
	}

	/**
	 * Fetch search results from the server based on the search term.
	 * 
	 * @param {string} searchTerm - The term to search for.
	 */
	getSearchResults(searchTerm) {
		if (this._suggestAbort) {
			this._suggestAbort.abort();
		}
		this._suggestAbort = new AbortController();
		const abortSignal = this._suggestAbort.signal;

		this._setPredictiveLoading(true);

		const q = encodeURIComponent(searchTerm);
		const localeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

		// Produits : recherche complète /search → prend en compte les metafields "searchable"
		// (config Search & Discovery), comme la page de résultats. options[prefix]=last garde le
		// matching au fil de la frappe sur le dernier terme.
		const productsUrl =
			`${localeRoot}search?q=${q}` +
			'&type=product' +
			'&options[prefix]=last' +
			'&options[unavailable_products]=last' +
			'&section_id=predictive-search-products';

		// Suggestions (articles + collections) : Predictive Search API (les metafields n'y changent rien).
		const suggestionsUrl =
			`${localeRoot}search/suggest?q=${q}&section_id=predictive-search` +
			'&resources[type]=article,collection' +
			'&resources[limit]=10';

		// Filet de sécurité : si /search échoue, retomber sur /search/suggest (comportement historique).
		const fetchProducts = () =>
			fetch(productsUrl, { signal: abortSignal })
				.then((r) => { if (!r.ok) throw new Error(r.status); return r.text(); })
				.then((text) => ({ text, sectionId: 'predictive-search-products' }))
				.catch((err) => {
					if (err && err.name === 'AbortError') throw err;
					const fallbackUrl =
						`${localeRoot}search/suggest?q=${q}&section_id=predictive-search` +
						'&resources[type]=product' +
						'&resources[limit]=10' +
						'&resources[options][unavailable_products]=last';
					return fetch(fallbackUrl, { signal: abortSignal })
						.then((r) => { if (!r.ok) throw new Error(r.status); return r.text(); })
						.then((text) => ({ text, sectionId: 'predictive-search' }));
				});

		Promise.all([
			fetchProducts(),
			fetch(suggestionsUrl, { signal: abortSignal }).then((r) => { if (!r.ok) throw new Error(r.status); return r.text(); }),
		])
			.then(([productsResult, suggestionsText]) => {
				if (!this.predictiveSearchResults) {
					this._setPredictiveLoading(false);
					return;
				}

				const productsDoc = new DOMParser().parseFromString(productsResult.text, 'text/html');
				const productsSectionRoot = productsDoc.querySelector('#shopify-section-' + productsResult.sectionId);
				if (!productsSectionRoot) {
					console.warn('[predictive-search] Section produits introuvable (#shopify-section-' + productsResult.sectionId + ') dans la réponse.');
					this._setPredictiveLoading(false);
					return;
				}

				// Injecter les produits
				this.predictiveSearchResults.innerHTML = productsSectionRoot.innerHTML;

				// Extraire les suggestions depuis le second appel
				const suggestionsDoc = new DOMParser().parseFromString(suggestionsText, 'text/html');
				const suggestionsSectionRoot = suggestionsDoc.querySelector('#shopify-section-predictive-search');
				const collections = suggestionsSectionRoot
					? [...suggestionsSectionRoot.querySelectorAll('[data-suggest-collection]')].map((el) => ({ title: el.dataset.title, url: el.dataset.url }))
					: [];
				const articles = suggestionsSectionRoot
					? [...suggestionsSectionRoot.querySelectorAll('[data-suggest-article]')].map((el) => ({ title: el.dataset.title, url: el.dataset.url }))
					: [];

				document.dispatchEvent(new CustomEvent('mm:search-suggestions', {
					detail: { collections, articles }
				}));

				this.open();
				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						window.setTimeout(() => {
							this._setPredictiveLoading(false);
						}, 80);
					});
				});
			})
			.catch((error) => {
				if (error && error.name === 'AbortError') {
					return;
				}
				this._setPredictiveLoading(false);
				this.close();
				throw error;
			});
	}

	/**
	 * Voile + spinner sur la zone résultats pendant le fetch / premier rendu des cartes.
	 */
	_setPredictiveLoading(active) {
		const el = this.predictiveSearchResults;
		if (!el) return;
		el.classList.toggle('mm-predictive-search-is-loading', active);
		el.setAttribute('aria-busy', active ? 'true' : 'false');
	}

	/**
	 * Open the predictive search results container.
	 */
	open() {
		this.predictiveSearchResults.style.display = 'block';
		this.predictiveSearchDefault.style.display = 'none';
	}

	/**
	 * Close the predictive search results container.
	 */
	close() {
		this._setPredictiveLoading(false);
		this.predictiveSearchResults.style.display = 'none';
		this.predictiveSearchDefault.style.display = 'flex';
	}

	/**
	 * Debounce function to limit the rate at which a function can fire.
	 * 
	 * @param {Function} fn - The function to debounce.
	 * @param {number} wait - The number of milliseconds to delay.
	 * @returns {Function} - The debounced function.
	 */
	debounce(fn, wait) {
		let t;
		return (...args) => {
			clearTimeout(t);
			t = setTimeout(() => fn.apply(this, args), wait);
		};
	}
}

// Define the custom element
customElements.define('predictive-search', PredictiveSearch);